import { useTranslation } from 'react-i18next';
import { LGP_02_VERSION } from '@aions/core';
import type { WizardData } from '../../pages/IntakeWizard';
import { identidadeIncompleta } from '../../lib/identidade-form';

interface Props {
  data: WizardData;
  onToggleIdentificacao: (v: boolean) => void;
  onIdentidadeChange: (partial: Partial<WizardData>) => void;
  onTogglePolicy: (v: boolean) => void;
}

export function Step4Revisao({
  data,
  onToggleIdentificacao,
  onIdentidadeChange,
  onTogglePolicy,
}: Props): JSX.Element {
  const { t } = useTranslation();
  // P10 — CPF opcional; se preenchido, valida formato (11 dígitos) client-side.
  const cpfDigits = data.cpf.replace(/\D/g, '');
  const cpfInvalido = data.cpf.trim() !== '' && cpfDigits.length !== 11;

  return (
    <div className="fade-in">
      <h2>{t('step4.review_title')}</h2>

      <div className="review-section">
        <div className="review-label">{t('wizard.step1_title')}</div>
        <div className="review-value">
          {data.categoria ? t(`categorias.${data.categoria}`) : '—'}
        </div>
      </div>

      <div className="review-section">
        <div className="review-label">{t('step2.descricao_label')}</div>
        <div className="review-value">{data.descricao || '—'}</div>
      </div>

      {(data.quando || data.onde || data.envolvidos) && (
        <div className="review-section">
          {data.quando && (
            <>
              <div className="review-label">{t('step2.when_label')}</div>
              <div className="review-value">{data.quando}</div>
            </>
          )}
          {data.onde && (
            <>
              <div className="review-label" style={{ marginTop: 12 }}>
                {t('step2.where_label')}
              </div>
              <div className="review-value">{data.onde}</div>
            </>
          )}
          {data.envolvidos && (
            <>
              <div className="review-label" style={{ marginTop: 12 }}>
                {t('step2.who_label')}
              </div>
              <div className="review-value">{data.envolvidos}</div>
            </>
          )}
        </div>
      )}

      {data.files.length > 0 && (
        <div className="review-section">
          <div className="review-label">{t('wizard.step3_title')}</div>
          <div className="review-value">{data.files.map((f) => f.name).join(', ')}</div>
        </div>
      )}

      {/* INT-08: Aviso de não-coleta */}
      <div className="alert alert-info">{t('step4.aviso_anonimato')}</div>

      {/* INT-09: Aviso de estilometria */}
      <div className="alert alert-warning">{t('step4.aviso_estilometria')}</div>

      {/* POL-01: Política de não-retaliação */}
      <div className="alert alert-info">
        {t('step4.aviso_nao_retaliacao')}{' '}
        <a href="#privacidade" className="footer-link" style={{ display: 'inline' }}>
          {t('step4.saiba_mais')}
        </a>
      </div>

      {/* INT-05: Identificação voluntária */}
      <div className="checkbox-wrapper">
        <input
          type="checkbox"
          id="identificacao-voluntaria"
          checked={data.identificacaoVoluntaria}
          onChange={(e) => onToggleIdentificacao(e.target.checked)}
        />
        <label htmlFor="identificacao-voluntaria">
          <strong>{t('step4.identificacao_voluntaria')}</strong>
          <br />
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            {t('step4.identificacao_nota')}
          </span>
        </label>
      </div>

      {/* P10 (ADR-0018) — campos só aparecem com o opt-in marcado. Nome + contato
          obrigatórios; CPF opcional. Tudo cifrado num bloco SEPARADO (a IA nunca vê).

          <fieldset> e não <div>: é um grupo de campos condicional, e a <legend>
          dá ao leitor de tela o contexto que o vidente recebe pela borda lateral. */}
      {data.identificacaoVoluntaria && (
        <fieldset className="identidade-panel">
          <legend className="identidade-panel__legend">{t('step4.identidade_legend')}</legend>

          {/* Quem vê / quem não vê, em duas linhas escaneáveis. Antes era um
              parágrafo único misturando cifra + destinatário + IA + CPF: tudo
              verdadeiro, nada legível na tela onde a pessoa mais precisa ler.
              Ícones decorativos (aria-hidden) — a palavra já carrega o sentido. */}
          <p className="identidade-quem">
            <span className="identidade-quem__icone" aria-hidden="true">
              👁
            </span>
            <span>
              <strong>{t('step4.identidade_quem_ve_label')}</strong> {t('step4.identidade_quem_ve')}
            </span>
          </p>
          <p className="identidade-quem">
            <span className="identidade-quem__icone" aria-hidden="true">
              🤖
            </span>
            <span>
              <strong>{t('step4.identidade_quem_nao_ve_label')}</strong>{' '}
              {t('step4.identidade_quem_nao_ve')}
            </span>
          </p>

          {/* Terceiro fato de confiança: o dado não sobrevive à aba. É o par
              visível da cura do vazamento no rascunho (draft-storage.ts) — sem
              isso, os campos voltarem vazios depois de um F5 pareceria bug. */}
          <p className="identidade-quem">
            <span className="identidade-quem__icone" aria-hidden="true">
              🗑
            </span>
            <span>{t('step4.identidade_nao_salvo')}</span>
          </p>

          <div className="form-group">
            <label className="form-label" htmlFor="id-nome">
              {t('step4.identidade_nome')} *
            </label>
            <input
              id="id-nome"
              type="text"
              className="form-input"
              value={data.nome}
              onChange={(e) => onIdentidadeChange({ nome: e.target.value })}
              autoComplete="off"
              aria-required="true"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="id-contato">
              {t('step4.identidade_contato')} *
            </label>
            <input
              id="id-contato"
              type="text"
              className="form-input"
              value={data.contato}
              onChange={(e) => onIdentidadeChange({ contato: e.target.value })}
              placeholder={t('step4.identidade_contato_placeholder')}
              autoComplete="off"
              aria-required="true"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="id-cpf">
              {t('step4.identidade_cpf')}
            </label>
            <input
              id="id-cpf"
              type="text"
              className="form-input"
              value={data.cpf}
              onChange={(e) => onIdentidadeChange({ cpf: e.target.value })}
              inputMode="numeric"
              autoComplete="off"
              aria-invalid={cpfInvalido}
              aria-describedby="id-cpf-ajuda"
            />
            {/* Ajuda discreta, NÃO alerta âmbar: assustar com vermelho num campo
                que a pessoa nem precisava preencher é pior que o erro. O texto
                muda quando o formato destoa, mas nunca bloqueia o envio. */}
            <p className="identidade-campo__ajuda" id="id-cpf-ajuda">
              {cpfInvalido ? t('step4.identidade_cpf_invalido') : t('step4.identidade_cpf_ajuda')}
            </p>
          </div>

          {/* 🟠 O botão Enviar desabilita no instante em que o opt-in é marcado.
              Antes isso acontecia em silêncio — a pessoa via o botão apagar sem
              nenhum texto na tela. role="status" para ser anunciado sem roubar
              foco. A regra vem de identidadeIncompleta (cópia única, a mesma que
              o IntakeWizard usa para o disabled). */}
          {identidadeIncompleta(data) && (
            <p className="identidade-pendencia" role="status">
              {t('step4.identidade_required_hint')}
            </p>
          )}

          {/* Saída simétrica à entrada: entrar na identificação custa 1 clique,
              sair custa 1 clique. É a peça que impede a tela de empurrar. Chama o
              MESMO handler do checkbox — quem limpa os campos é o wizard, num
              lugar só. */}
          <button
            type="button"
            className="identidade-anonimo-btn"
            onClick={() => onToggleIdentificacao(false)}
          >
            {t('step4.identidade_voltar_anonimo')}
          </button>
        </fieldset>
      )}

      {/* Sprint 3.1 — Aceite obrigatório LGP-02 (sem aceite, submit fica disabled). */}
      <div
        className="checkbox-wrapper"
        style={{
          marginTop: '1rem',
          padding: '0.75rem',
          border: '2px solid',
          borderColor: data.policyAccepted ? 'var(--color-success, #16a34a)' : '#fbbf24',
          borderRadius: '0.375rem',
          background: data.policyAccepted ? 'transparent' : '#fef3c7',
        }}
      >
        <input
          type="checkbox"
          id="policy-accepted"
          checked={data.policyAccepted}
          onChange={(e) => onTogglePolicy(e.target.checked)}
          required
        />
        <label htmlFor="policy-accepted">
          <strong>
            {t('step4.policy_label', { version: LGP_02_VERSION })} <span aria-hidden="true">*</span>
          </strong>
          <br />
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            {t('step4.policy_help')}{' '}
            <a href="#treinamento" className="footer-link" style={{ display: 'inline' }}>
              {t('step4.policy_link')}
            </a>
            .
          </span>
        </label>
      </div>
    </div>
  );
}
